import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Box, Card, CardContent, Chip, CircularProgress, Divider, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  InputAdornment, Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import * as hierarchyApi from '../api/hierarchy';

/**
 * Everyone working beneath this node — providers, staff and clinic administrators.
 *
 * Staffing, not clinical. It answers "who works where", and touches no patient row, so
 * any level may read it from where it stands without opening anything.
 */

// A person can hold several roles at one office — a provider who is also its
// administrator — so each row shows all of them, not just the tab it was found under.
const ROLE_CHIPS = [
  { key: 'clinic_admins', label: 'Admin', color: 'warning' },
  { key: 'providers', label: 'Provider', color: 'primary' },
  { key: 'staff', label: 'Staff', color: 'default' },
];

const TABS = [
  { key: 'providers', label: 'Providers' },
  { key: 'staff', label: 'Staff' },
  { key: 'clinic_admins', label: 'Clinic admins' },
];

export default function NodePeople() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await hierarchyApi.getPeople());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load people');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!data) return null;

  const key = TABS[tab].key;
  // (person, office) → every role they hold there, across all three lists.
  const rolesAt = {};
  ROLE_CHIPS.forEach((r) => (data[r.key] || []).forEach((row) => {
    const k = `${row.user_id}-${row.office_id}`;
    (rolesAt[k] = rolesAt[k] || new Set()).add(r.key);
  }));
  const q = search.trim().toLowerCase();
  const rows = (data[key] || []).filter((r) => !q
    || r.name.toLowerCase().includes(q)
    || (r.email || '').toLowerCase().includes(q)
    || (r.office_name || '').toLowerCase().includes(q));

  return (
    <Box>
      <Typography variant="h4" fontWeight={700}>People</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Everyone working across {data.node?.name}. Who works where — no patient
        information appears on this page.
      </Typography>

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
          {TABS.map((t) => (
            // People, not rows: someone at two offices has a row at each, and counting
            // rows made the Providers tab disagree with the Team page.
            <Tab key={t.key} label={`${t.label} (${new Set((data[t.key] || []).map((r) => r.user_id)).size})`} />
          ))}
        </Tabs>
        <Divider />
        <CardContent sx={{ pb: 1 }}>
          <TextField fullWidth size="small" placeholder="Search by name, email or office…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ) }} />
        </CardContent>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Office</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!rows.length ? (
                <TableRow><TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    {q ? 'Nobody matches that search.' : `No ${TABS[tab].label.toLowerCase()} yet.`}
                  </Typography>
                </TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={`${r.user_id}-${r.office_id}`} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{r.name}</Typography></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{r.email}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{r.office_name}</Typography></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {ROLE_CHIPS.filter((c) => rolesAt[`${r.user_id}-${r.office_id}`]?.has(c.key)).map((c) => (
                        <Chip key={c.key} size="small" label={c.label} color={c.color}
                          variant={c.color === 'default' ? 'outlined' : 'filled'} sx={{ height: 22 }} />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={r.is_active ? 'Active' : 'Inactive'}
                      color={r.is_active ? 'success' : 'default'} sx={{ height: 22 }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
